import { useState } from 'react';
import { rsme, type Quotation } from '../lib/rsme';
import { useQuery } from '../ui/useQuery';
import { PageHead, Card, Pill, Input, Loading, ErrorState, EmptyState, Button } from '../ui/primitives';
import { useAiko } from '../aiko/store';

const STATUSES: Quotation['status'][] = ['draft', 'sent', 'accepted', 'rejected', 'expired'];

export default function Quotations() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<Quotation['status'] | ''>('');
  const ask = useAiko((s) => s.ask);
  const q = useQuery(() => rsme.quotations.list({ search, status: status || undefined }), [search, status]);

  return (
    <div className="max-w-5xl mx-auto">
      <PageHead
        title="Quotations"
        sub="Drafts, sent, won, lost"
        action={<Button onClick={() => ask('Help me draft a new quotation. Ask me what I need.')}>Draft with Aiko</Button>}
      />
      <div className="px-5 md:px-8 flex flex-wrap gap-2 items-center">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search quote no, company, subject" className="md:max-w-sm" />
        <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="h-10 px-3 rounded-lg bg-elevated border border-line text-sm">
          <option value="">all</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="px-5 md:px-8 mt-4 space-y-2">
        {q.loading && <Loading rows={5} />}
        {q.error && <ErrorState error={q.error} retry={q.refetch} />}
        {!q.loading && !q.error && !q.data?.data?.length && <EmptyState title="No quotations" />}
        {q.data?.data?.map((qq) => (
          <Card key={qq.id} className="!p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-semibold tabular-nums">{qq.quotationNo}</span>
              <Pill tone={qq.status === 'accepted' ? 'good' : qq.status === 'sent' ? 'accent' : qq.status === 'rejected' || qq.status === 'expired' ? 'danger' : 'muted'}>{qq.status}</Pill>
              <span className="text-sm truncate flex-1">{qq.companyName}</span>
              <span className="text-sm tabular-nums">{qq.totalAmount ? `$${qq.totalAmount.toLocaleString()}` : '—'}</span>
            </div>
            {qq.subject && <div className="text-xs text-muted mt-1 truncate">{qq.subject}</div>}
            <div className="text-xs text-muted mt-1">{qq.salesPerson ?? '—'} · sent {qq.dateOfComm ?? '—'} · valid {qq.validity ?? '—'}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}
